import Debit, { DebitHistoryItem, DEBIT_METHOD_BANK } from '@/models/Debit';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import { BankHistoryItemDtoIn } from '@/models/StandingOrderDetails';
import {
  BANK_TRANSACTION_TYPE_RETURN,
  STANDING_ORDER_TYPE_BANK,
  STANDING_ORDER_TYPE_CREDIT,
  UNLIMITED_PAYMENTS,
  isUnlimited,
} from './standingOrderModel';

/*
 * The accounting system's standing orders on the board (HAT-1135).
 *
 * The office keeps every bank standing order - and a few credit-card ones it charges itself - in
 * APT, not in Nedarim. The site already reads that list (`funds/financial-data/{id}/debits`; the old
 * site's «הוראות קבע» page is built on it). The board's rows, filters, summary, card and blade all
 * speak the Nedarim DTO, so an APT order is translated into that shape here, once, and marked
 * `mosadSource: "Apt"` so the few places that must behave differently (no charge, no card link, a
 * projected rather than reported next date) can tell.
 *
 * Rules learned from the data (HAT-1309, 17.9.2026): the cycle is monthly, always; APT writes no
 * charge ahead of time, so the next one is projected from the billing day; «פעיל» alone is noise
 * (a fund had 276 "active" orders of which 151 collect) - active AND not out of payments AND not
 * past its end is what matches the real collections; the «סטטוס» text is stale for liveness but
 * does tell whether the bank has confirmed a new mandate («אושר בבנק») and, on a stopped order,
 * why the office stopped it (HAT-1439).
 */

export const APT_MOSAD_SOURCE = 'Apt';

/** The office's status text once the bank has confirmed the mandate. */
const APT_STATUS_BANK_APPROVED = 'אושר בבנק';

/**
 * Status texts that describe a running order, not why it stopped. The office types «סטטוס» by hand
 * (APT labels it «מצב הוראה») and does not always clear it when it flips «פעיל», so a stopped order
 * can still read «פעיל» (one on fund 130, dev, 14.10.2026) - that is no reason to show.
 */
const APT_ACTIVE_STATUS_TEXTS = new Set(['פעיל', APT_STATUS_BANK_APPROVED]);

/**
 * What the board itself writes into «סטטוס» when it stops an order (HAT-1441). Must match
 * AptBankOrderStatuses on the server. The office's own words are kept as they are; these two are
 * the board's, and the board lifts its own stops - a freeze or a cancellation - and only those.
 * The coordinator's reason, when given, follows the word: «הוקפא מהאתר: לבקשת התורם».
 */
export const APT_STATUS_FROZEN_FROM_SITE = 'הוקפא מהאתר';
export const APT_STATUS_CANCELLED_FROM_SITE = 'בוטל מהאתר';

/** Must match AptBankOrderStatuses.MaxReasonLength: what fits of a reason in «סטטוס» after the word. */
export const APT_STOP_REASON_MAX_LENGTH = 35;

/** Whether a status text is the board's word, with or without a reason after it. */
export const isBoardWord = (status: string | null | undefined, word: string): boolean => {
  const text = (status ?? '').trim();
  return text === word || text.startsWith(`${word}:`);
};

/** The reason the board wrote after its word, or null. */
export const boardStopReason = (status: string | null | undefined): string | null => {
  const text = (status ?? '').trim();
  for (const word of [APT_STATUS_FROZEN_FROM_SITE, APT_STATUS_CANCELLED_FROM_SITE]) {
    if (text.startsWith(`${word}:`)) return text.slice(word.length + 1).trim() || null;
  }
  return null;
};

const isSiteStatus = (debit: Pick<Debit, 'isActive' | 'status'>, word: string): boolean =>
  !debit.isActive && isBoardWord(debit.status, word);

export const isAptOrder = (row: Pick<NedarimStandingOrderDtoIn, 'mosadSource'>): boolean =>
  row.mosadSource === APT_MOSAD_SOURCE;

const pad = (n: number) => String(n).padStart(2, '0');

/** dd/MM/yyyy - the only date format the board's model reads. */
export const toBoardDate = (date: Date): string =>
  `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;

/**
 * Local-midnight reading of the server's ISO date. `new Date('2026-09-01T00:00:00')` is already
 * local, but the server sometimes appends nothing and sometimes a time; strip both to the day so a
 * value date never slides across midnight.
 */
const readServerDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return isNaN(d.getTime()) ? null : d;
};

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
/** The given day of a month, or its last day when the month is shorter. */
const dayOfMonth = (year: number, month: number, day: number): Date => {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, lastDay));
};
const sameMonth = (a: Date, b: Date): boolean => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

const eventsSoFar = (debit: Debit): number => debit.countCharged + debit.countRejected;

const isOutOfPayments = (debit: Debit): boolean =>
  !isUnlimited(debit.numberOfPayments) && debit.numberOfPayments > 0 && eventsSoFar(debit) >= debit.numberOfPayments;

const isPastEnd = (debit: Debit, today: Date): boolean => {
  const endsAt = readServerDate(debit.endsAt);
  return endsAt != null && endsAt < today;
};

export type AptLiveness = 'live' | 'awaitingBank' | 'disabled' | 'ended';

/**
 * Whether the office will still send this order to the bank. `ended` wins over `disabled`: an
 * order that ran its course is finished whatever the flag says, and «לא פעילה» is reserved for one
 * the office stopped early.
 */
export const aptLiveness = (debit: Debit, now: Date = new Date()): AptLiveness => {
  const today = startOfDay(now);
  if (isOutOfPayments(debit)) return 'ended';
  // An end date that passed while the board had the order stopped does not end it: reactivating it
  // moves the end so the payments left still fit (HAT-1441).
  const stoppedByBoard = isSiteStatus(debit, APT_STATUS_FROZEN_FROM_SITE) || isSiteStatus(debit, APT_STATUS_CANCELLED_FROM_SITE);
  if (isPastEnd(debit, today) && !stoppedByBoard) return 'ended';
  if (!debit.isActive) return 'disabled';
  if (eventsSoFar(debit) === 0 && (debit.status ?? '').trim() !== APT_STATUS_BANK_APPROVED) return 'awaitingBank';
  return 'live';
};

/**
 * Why the office stopped the order, in its own words, or null (HAT-1439). APT keeps it in «סטטוס»
 * - the field its order screen calls «מצב הוראה» - and the date it was set in «תאריך_סטטוס»; its
 * separate «סיבת_הקפאה» / «סיבת_ביטול» fields are never filled (0 of 783 bank orders on fund 130).
 * Only a stopped order gets one: a running order often still carries the text of an old stop
 * («ביטול הרשאה» on 9 active orders of fund 130), which is history, not a reason.
 */
export const aptStopReason = (debit: Debit, now: Date = new Date()): string | null => {
  if (aptLiveness(debit, now) !== 'disabled') return null;
  const status = debit.status?.trim();
  // A stop made from the board is not the office's reason: the blade says who stopped it and when.
  if (isBoardWord(status, APT_STATUS_FROZEN_FROM_SITE) || isBoardWord(status, APT_STATUS_CANCELLED_FROM_SITE)) return null;
  return status && !APT_ACTIVE_STATUS_TEXTS.has(status) ? status : null;
};

/**
 * When the bank next collects, or null when it will not: monthly on the billing day, skipping a
 * month already collected, never past the end. The same rule «תנועות עתידיות» projects with.
 */
export const nextAptCharge = (debit: Debit, now: Date = new Date()): Date | null => {
  const liveness = aptLiveness(debit, now);
  if (liveness === 'disabled' || liveness === 'ended') return null;
  const day = debit.billingDayOfMonth;
  if (!day || day < 1 || day > 31) return null;

  const today = startOfDay(now);
  let candidate = dayOfMonth(today.getFullYear(), today.getMonth(), day);
  const lastCharge = readServerDate(debit.lastChargeAt);
  if (candidate <= today || (lastCharge && sameMonth(lastCharge, candidate))) {
    candidate = dayOfMonth(today.getFullYear(), today.getMonth() + 1, day);
  }
  const endsAt = readServerDate(debit.endsAt);
  if (endsAt && candidate > endsAt) return null;
  return candidate;
};

/** «12-533-8797334»: the old site's spelling, kept so a coordinator recognises the account. */
export const formatAptAccount = (debit: Debit): string | null => {
  const parts = [debit.bank, debit.branch, debit.account].map((p) => (p ?? '').trim());
  if (parts.every((p) => p === '')) return debit.billingAccount?.trim() || null;
  return parts.join('-');
};

/** «ויזה ····0633» for a credit order the office charges itself; the card company is all APT holds. */
const creditCardLastDigits = (debit: Debit): string | null => {
  const m = (debit.billingAccount ?? '').match(/(\d{4})\s*$/);
  return m ? m[1] : null;
};

/**
 * One APT order as a board row. Everything the board reads off a Nedarim bank row is filled from
 * the accounting fields; what APT does not know (phone, mail, city, Nedarim's own ids) stays null.
 */
export const aptDebitToStandingOrder = (debit: Debit, now: Date = new Date()): NedarimStandingOrderDtoIn => {
  const isBank = debit.paymentMethod === DEBIT_METHOD_BANK;
  const liveness = aptLiveness(debit, now);
  const events = eventsSoFar(debit);

  const remainingPayments = liveness === 'ended'
    ? 0
    : isUnlimited(debit.numberOfPayments) || debit.numberOfPayments <= 0
      ? UNLIMITED_PAYMENTS
      : Math.max(0, debit.numberOfPayments - events);

  const next = nextAptCharge(debit, now);

  const lastCharge = readServerDate(debit.lastChargeAt);
  const lastReturn = readServerDate(debit.lastRejectionAt);
  const lastEventIsReturn = lastReturn != null && (lastCharge == null || lastReturn >= lastCharge);
  const lastEvent = lastEventIsReturn ? lastReturn : lastCharge;

  const startDate = readServerDate(debit.startDate);
  const statusDate = readServerDate(debit.statusDate);

  return {
    standingOrderId: debit.donationId,
    type: isBank ? STANDING_ORDER_TYPE_BANK : STANDING_ORDER_TYPE_CREDIT,
    clientName: [debit.lastName, debit.firstName].filter((s) => s && s.trim()).join(' ').trim() || null,
    israeliId: null,
    phone: null,
    email: null,
    amount: Math.abs(debit.monthlyAmount),
    nextDate: next ? toBoardDate(next) : null,
    remainingPayments,
    comments: null,

    city: null,
    // Every APT standing order is in shekels; the accounting rows behind it carry no currency.
    currency: '1',
    paymentsSucceeded: isBank ? null : debit.countCharged,
    terminalId: null,
    creationDate: isBank ? null : (startDate ? toBoardDate(startDate) : null),
    // The board shows a rejection's reason off this field whatever the row's type.
    errorMessage: lastEventIsReturn ? (debit.rejectionReasonExample?.trim() || null) : null,
    validity: null,
    enabled: liveness === 'disabled' ? 'Disabled' : 'Enabled',
    lastNum: isBank ? null : creditCardLastDigits(debit),

    accountDetails: isBank ? formatAptAccount(debit) : null,
    billingDay: debit.billingDayOfMonth > 0 ? String(debit.billingDayOfMonth) : null,
    numberOfBroadcasts: events,
    totalBroadcasts: null,
    numberOfReturns: debit.countRejected,
    totalReturns: null,
    neto: String(debit.amountCharged),
    date: startDate ? toBoardDate(startDate) : null,
    /*
     * The date of the last collection - also when the bank returned it. The returned collection is
     * itself a row of the order (the return is a separate movement dated days later), and «חיוב
     * אחרון» is about the collection: the reporter asked for «1.10 with a red dot», not the day the
     * bank sent it back (HAT-1135, 6.10.2026). The return's own date is in the blade.
     */
    lastTransactionDate: (lastCharge ?? lastEvent) ? toBoardDate((lastCharge ?? lastEvent)!) : null,
    lastTransactionType: lastEvent ? (lastEventIsReturn ? BANK_TRANSACTION_TYPE_RETURN : 'שידור') : null,

    extraProperties: null,
    mosadSource: APT_MOSAD_SOURCE,
    // The server sends 0 for an order whose donor card it could not find.
    aptContactId: debit.contactId > 0 ? debit.contactId : null,
    aptStatus: debit.status?.trim() || null,
    aptStatusDate: statusDate ? toBoardDate(statusDate) : null,
    aptStopReason: aptStopReason(debit, now),
    aptAwaitingBank: liveness === 'awaitingBank',
    aptFrozenFromSite: isSiteStatus(debit, APT_STATUS_FROZEN_FROM_SITE),
    aptCancelledFromSite: isSiteStatus(debit, APT_STATUS_CANCELLED_FROM_SITE),
    aptStoppedByOffice: !debit.isActive
      && !isSiteStatus(debit, APT_STATUS_FROZEN_FROM_SITE)
      && !isSiteStatus(debit, APT_STATUS_CANCELLED_FROM_SITE),
    aptOutOfPayments: isOutOfPayments(debit),
    aptBoardStopReason: !debit.isActive ? boardStopReason(debit.status) : null,
    aptBank: isBank ? (debit.bank?.trim() || null) : null,
    aptBranch: isBank ? (debit.branch?.trim() || null) : null,
    aptAccount: isBank ? (debit.account?.trim() || null) : null,
    aptAccountHolder: isBank ? (debit.accountHolder?.trim() || null) : null,
  };
};

/** The history endpoint's rows in the shape the blade's bank table already draws. */
export const aptHistoryToBankHistory = (
  items: DebitHistoryItem[],
  t: (key: string, options?: Record<string, unknown>) => string,
): BankHistoryItemDtoIn[] =>
  items.map((item) => {
    const date = readServerDate(item.date);
    const returned = item.kind === 'Returned';
    const returnedAt = readServerDate(item.returnedAt);
    return {
      date: date ? toBoardDate(date) : item.date,
      amount: item.amount,
      transactionType: t(returned ? 'standingOrders.historyReturned' : 'standingOrders.historyCharged'),
      // A returned collection says when the bank sent it back, and why when the bank said.
      comments: returned
        ? [
          returnedAt ? t('standingOrders.historyReturnedOn', { date: toBoardDate(returnedAt).replace(/\//g, '.') }) : t('standingOrders.historyReturned'),
          item.reason?.trim(),
        ].filter(Boolean).join(' · ')
        : (item.receiptId ? t('standingOrders.receiptNumber', { id: item.receiptId }) : ''),
    };
  });

/** Ending within two payments or 60 days: the bank counterpart of a card about to expire. */
export const ENDING_SOON_DAYS = 60;
export const isAptEndingSoon = (row: NedarimStandingOrderDtoIn, now: Date = new Date()): boolean => {
  if (!isAptOrder(row)) return false;
  const remaining = row.remainingPayments;
  if (remaining == null || isUnlimited(remaining) || remaining <= 0) return false;
  if (remaining <= 2) return true;
  const next = row.nextDate ? row.nextDate.split('/').map(Number) : null;
  if (!next || next.length !== 3) return false;
  const end = new Date(next[2], next[1] - 1 + (remaining - 1), next[0]);
  return (end.getTime() - startOfDay(now).getTime()) / 86_400_000 <= ENDING_SOON_DAYS;
};
